(() => {
  if (window.__almaDawnCart) return;
  window.__almaDawnCart = true;
  const form = () => document.getElementById('CartPageForm');
  let cleanupControls = () => {};
  let syncControls = () => {};
  let renderVersion = 0;

  async function request(url, options = {}, format = 'json', timeout = 6000) {
    const controller = new AbortController();
    let timer;
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, { ...options, signal: controller.signal });
          const data = format === 'text' ? await response.text() : await response.json();
          if (!response.ok || (format === 'json' && data?.status >= 400)) {
            const error = new Error(data?.description || data?.message || window.almaDawn.networkError);
            error.rejected = response.status >= 400 && response.status < 500;
            throw error;
          }
          return data;
        })(),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error(window.almaDawn.networkError));
          }, timeout);
        })
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  function failure(message, blocked = true) {
    const page = document.querySelector('.alma-cart-page');
    if (!page) return;
    if (blocked) form()?.setAttribute('data-cart-render-error', 'true');
    let node = page.querySelector('.alma-cart-error');
    if (!node) {
      node = document.createElement('div');
      node.className = 'alma-cart-error';
      node.setAttribute('role', 'alert');
      node.setAttribute('tabindex', '-1');
      page.prepend(node);
    }
    node.replaceChildren();
    const text = document.createElement('p');
    text.textContent = blocked ? (page.dataset.almaRecovery || window.almaDawn.networkError) : message;
    node.append(text);
    if (blocked) {
      const reload = document.createElement('a');
      reload.href = window.location.href;
      reload.className = 'button button--secondary';
      reload.textContent = page.dataset.almaReload || window.almaDawn.networkError;
      node.append(reload);
    }
    syncControls();
    node.focus({ preventScroll: true });
    node.scrollIntoView({ block: 'nearest' });
  }

  function mountControls() {
    cleanupControls();
    syncControls = () => {};
    const f = form();
    if (!f) return;
    const bar = f.querySelector('[data-alma-mobile-checkout]');
    const primary = f.querySelector('.cart__checkout');
    const media = window.matchMedia('(max-width: 749px)');
    const disposers = [];
    let frame = 0;
    const on = (target, type, listener) => {
      target.addEventListener(type, listener, { passive: true });
      disposers.push(() => target.removeEventListener(type, listener));
    };
    const sync = () => {
      if (!f.isConnected) return;
      const blocked = Boolean(window.__almaCartPending) || f.hasAttribute('aria-busy') || f.hasAttribute('data-cart-render-error');
      f.querySelectorAll('button[name="checkout"]').forEach(button => { button.disabled = blocked; });
      f.querySelectorAll('[data-alma-express]').forEach(node => { node.inert = blocked; });
      if (!bar || !primary) return;
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const rect = primary.getBoundingClientRect();
      const visible = rect.height > 0 && rect.top >= top && rect.bottom <= bottom;
      const focused = document.activeElement;
      const editing = focused && f.contains(focused) && (focused.matches('textarea, select, [contenteditable="true"]') ||
        (focused.matches('input') && !/^(checkbox|radio|button|submit|hidden)$/.test(focused.type)));
      bar.hidden = !media.matches || visible || f.hidden || Boolean(editing);
      const total = bar.querySelector('[data-alma-mobile-total]');
      const subtotal = f.querySelector('[data-subtotal]');
      if (total && subtotal) total.textContent = subtotal.textContent;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; sync(); });
    };
    syncControls = sync;
    const mutations = new MutationObserver(sync);
    mutations.observe(f, { attributes: true, attributeFilter: ['hidden', 'aria-busy', 'data-cart-render-error'] });
    disposers.push(() => mutations.disconnect());
    if (primary && 'IntersectionObserver' in window) {
      const intersection = new IntersectionObserver(schedule, { threshold: [0, 1] });
      intersection.observe(primary);
      disposers.push(() => intersection.disconnect());
    }
    on(window, 'resize', schedule);
    on(window, 'scroll', schedule);
    on(f, 'focusin', schedule);
    on(f, 'focusout', schedule);
    on(media, 'change', schedule);
    if (window.visualViewport) {
      on(window.visualViewport, 'resize', schedule);
      on(window.visualViewport, 'scroll', schedule);
    }
    cleanupControls = () => {
      disposers.forEach(dispose => dispose());
      if (frame) cancelAnimationFrame(frame);
    };
    sync();
  }

  function updateHeader(cart, version) {
    const header = document.getElementById('cart-icon-bubble');
    if (!header) return;
    const apply = html => {
      if (!html || version !== renderVersion) return;
      const contents = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (contents && header.isConnected) header.innerHTML = contents.innerHTML;
    };
    const bundled = cart?.sections?.['cart-icon-bubble'];
    if (bundled) { apply(bundled); return; }
    request(window.almaDawn.cartUrl + '?section_id=cart-icon-bubble',
      { credentials: 'same-origin', cache: 'no-store' }, 'text').then(apply).catch(() => {});
  }

  async function refresh(cart) {
    const page = document.querySelector('.alma-cart-page');
    const section = page?.closest('.shopify-section');
    if (!section) throw new Error(window.almaDawn.networkError);
    const id = section.id.replace('shopify-section-', '');
    try {
      let html = cart?.sections?.[id];
      if (!html) {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', id);
        html = await request(url, { credentials: 'same-origin', cache: 'no-store' }, 'text');
      }
      const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector('.alma-cart-page');
      if (!replacement) throw new Error(window.almaDawn.networkError);
      const replacementForm = replacement.querySelector('#CartPageForm');
      if (window.__almaCartPending && replacementForm) replacementForm.setAttribute('aria-busy', 'true');
      page.replaceWith(replacement);
      renderVersion += 1;
      mountControls();
      document.dispatchEvent(new CustomEvent('cart:rendered'));
      updateHeader(cart, renderVersion);
    } catch (error) {
      failure(error.message);
      throw error;
    }
  }

  Object.assign(window.theme.cart, { refreshPage: refresh, request, showError: failure, syncControls: () => syncControls() });

  async function update(input, quantity) {
    if (window.__almaCartPending || form()?.hasAttribute('data-cart-render-error')) return;
    const f = form();
    if (!f) return;
    const id = f.closest('.shopify-section').id.replace('shopify-section-', '');
    window.__almaCartPending = true;
    f.setAttribute('aria-busy', 'true');
    syncControls();
    let changed = false;
    try {
      const cart = await request(window.almaDawn.cartUrl + '/change.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: input.dataset.id, quantity, sections: [id, 'cart-icon-bubble'], sections_url: window.location.pathname })
      }, 'json', 12000);
      changed = true;
      await refresh(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (error) {
      if (changed || error.rejected) {
        try {
          await refresh();
          document.dispatchEvent(new CustomEvent('cart:updated'));
          if (error.rejected) failure(error.message, false);
        } catch (_) {}
      } else {
        failure(error.message);
      }
    } finally {
      window.__almaCartPending = false;
      form()?.removeAttribute('aria-busy');
      syncControls();
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('.alma-cart-page .js-qty__adjust');
    const remove = event.target.closest('.alma-cart-page .cart__remove a');
    if (!button && !remove) return;
    event.preventDefault();
    const input = (button || remove).closest('.cart__item').querySelector('.js-qty__num');
    if (!input) return;
    const quantity = remove ? 0 : Math.max(0, Number(input.value) + (button.classList.contains('js-qty__adjust--plus') ? 1 : -1));
    update(input, quantity);
  });
  document.addEventListener('change', event => {
    if (event.target.matches('.alma-cart-page .js-qty__num')) update(event.target, Math.max(0, Math.floor(Number(event.target.value) || 0)));
  });
  document.addEventListener('submit', event => {
    if (event.target.id !== 'CartPageForm') return;
    if (window.__almaCartPending || event.target.hasAttribute('data-cart-render-error')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    queueMicrotask(() => {
      if (!event.defaultPrevented) event.target.querySelector('.alma-mobile-checkout__button')?.classList.add('btn--loading');
    });
  }, true);
  window.addEventListener('pageshow', () => {
    form()?.querySelectorAll('.btn--loading').forEach(button => button.classList.remove('btn--loading'));
    mountControls();
  });
  document.addEventListener('shopify:section:load', mountControls);
  mountControls();
})();
